/**
 * Utility for handling rate-limited requests with automatic retry
 * Detects rate limit errors (HTTP 429, Firebase resource-exhausted, etc.) and retries with exponential backoff
 */

// Helper function for delays
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if an error is a rate limit error
 * @param {Error|any} error - The error to check
 * @returns {boolean} - True if the error indicates rate limiting
 */
export const isRateLimitError = (error) => {
  if (!error) return false;
  
  // Check HTTP status code (429 = Too Many Requests)
  if (error.status === 429 || error.code === 429 || error.statusCode === 429) {
    return true;
  }
  
  // Check Firebase error codes
  const code = error?.code?.toLowerCase() || '';
  const firebaseRateLimitCodes = [
    'resource-exhausted', // Firebase rate limit
    'unavailable', // Sometimes indicates rate limiting
    'deadline-exceeded' // Can indicate rate limiting
  ];
  
  if (firebaseRateLimitCodes.includes(code)) {
    return true;
  }
  
  // Check error message for rate limit indicators
  const message = (error?.message || error?.reason || error?.toString() || '').toLowerCase();
  const rateLimitIndicators = [
    'rate limit',
    'too many requests',
    'rate exceeded',
    'quota exceeded',
    'quota limit',
    'throttle',
    'throttled',
    '429'
  ];
  
  for (const indicator of rateLimitIndicators) {
    if (message.includes(indicator)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Execute a function with automatic retry on rate limit errors
 * @param {Function} fn - The async function to execute
 * @param {Object} options - Retry options
 * @param {number} options.retries - Maximum number of retries (default: 5)
 * @param {number} options.baseDelayMs - Base delay in milliseconds for exponential backoff (default: 1000)
 * @param {number} options.maxDelayMs - Maximum delay in milliseconds (default: 30000)
 * @param {Function} options.onRetry - Optional callback called before each retry (attempt, delay, error)
 * @returns {Promise<any>} - The result of the function
 * @throws {Error} - The last error if all retries fail
 */
export const withRateLimitRetry = async (
  fn,
  {
    retries = 5,
    baseDelayMs = 1000,
    maxDelayMs = 30000,
    onRetry = null
  } = {}
) => {
  let lastError;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Check if this is a rate limit error
      const isRateLimited = isRateLimitError(error);
      
      // If it's not a rate limit error or we've exhausted retries, throw immediately
      if (!isRateLimited || attempt === retries) {
        throw error;
      }
      
      // Calculate delay with exponential backoff, capped at maxDelayMs
      const delay = Math.min(
        baseDelayMs * Math.pow(2, attempt),
        maxDelayMs
      );
      
      // Log retry attempt
      console.warn(
        `Rate limited, retrying in ${delay}ms (attempt ${attempt + 1}/${retries + 1})`,
        error
      );
      
      // Call onRetry callback if provided
      if (onRetry) {
        try {
          onRetry(attempt + 1, delay, error);
        } catch (callbackError) {
          console.error('Error in onRetry callback:', callbackError);
        }
      }
      
      // Wait before retrying
      await sleep(delay);
    }
  }
  
  // Should never reach here, but just in case
  throw lastError || new Error('Failed after all retries');
};

